/** Small structural view of the generated, non-normalized control snapshot. */
export interface MitsubishiPowerSnapshot {
  controlContract: string;
  powerMode?: { value?: string | null; pending?: boolean | null } | null;
  powerState?: {
    value?: string | null;
    observedAt?: number | null;
  } | null;
  operatingMode?: {
    value?: string | null;
    observedAt?: number | null;
    pending?: boolean | null;
  } | null;
  operatingState?: { value?: string | null } | null;
  fanSpeed?: { value?: string | null; pending?: boolean | null } | null;
  airDirection?: {
    value?: string | null;
    pending?: boolean | null;
    observedAt?: number | null;
  } | null;
  supportsAirDirection?: boolean;
  canEditAirDirection?: boolean;
  allowedAirDirections?: string[] | null;
  canEditFanSpeed?: boolean;
  allowedFanSpeeds?: string[] | null;
  canEditPowerMode: boolean;
  canEditOperatingMode: boolean;
  allowedOperatingModes?: string[] | null;
  canEditTargets: boolean;
  targetBlockReason?: string | null;
}

export function getMitsubishiOperatingModes(
  snapshot: MitsubishiPowerSnapshot,
): string[] {
  return (snapshot.allowedOperatingModes ?? ['Heat', 'Cool', 'Auto']).filter(
    (mode) => ['Heat', 'Cool', 'Auto', 'Fan'].includes(mode),
  );
}

export function effectiveSnapshotPower(
  snapshot: MitsubishiPowerSnapshot,
): 'On' | 'Off' | 'Unknown' {
  const value = snapshot.powerState?.value;
  return value === 'On' || value === 'Off' ? value : 'Unknown';
}

export function getMitsubishiTargetEditBlockReason(
  snapshot: MitsubishiPowerSnapshot,
  localPending = false,
  draft?: { powerMode?: string | null; operatingMode?: string | null },
): string | null {
  if (
    localPending ||
    snapshot.powerMode?.pending ||
    snapshot.operatingMode?.pending ||
    snapshot.fanSpeed?.pending ||
    snapshot.airDirection?.pending
  )
    return 'Wait for the pending command to be confirmed.';
  const mode = draft?.operatingMode ?? snapshot.operatingMode?.value;
  if (mode === 'Fan') return 'Fan only does not use temperature targets.';
  const power = draft?.powerMode ?? snapshot.powerMode?.value;
  if (power !== 'On' && power !== 'Off')
    return 'Reported power selection is unavailable.';
  if (power === 'Off') return 'Turn power on to edit temperature targets.';
  if (!['Heat', 'Cool', 'Auto'].includes(mode ?? ''))
    return 'Select a temperature operating mode.';
  if (!snapshot.canEditTargets)
    return snapshot.targetBlockReason ?? 'Target editing is not available.';
  return null;
}

export function getMitsubishiFanSpeeds(
  snapshot: MitsubishiPowerSnapshot,
): string[] {
  const speeds = snapshot.allowedFanSpeeds;
  const canonical = ['Auto', 'Low', 'Medium2', 'Medium1', 'High'];
  if (
    !Array.isArray(speeds) ||
    speeds.some((speed) => !canonical.includes(speed))
  )
    return [];
  return canonical.filter((speed) => speeds.includes(speed));
}

export function mitsubishiFanSpeedLabel(speed: string): string {
  return speed === 'Medium1'
    ? 'Medium 1 (higher medium)'
    : speed === 'Medium2'
      ? 'Medium 2 (lower medium)'
      : speed;
}

const airDirections = [
  'Auto',
  'Swing',
  'Horizontal',
  'Mid0',
  'Mid1',
  'Mid2',
  'Vertical',
];
export function getMitsubishiAirDirections(
  snapshot: Pick<MitsubishiPowerSnapshot, 'allowedAirDirections'>,
): string[] {
  const values = snapshot.allowedAirDirections;
  if (
    !Array.isArray(values) ||
    values.some((value) => !airDirections.includes(value))
  )
    return [];
  return airDirections.filter((value) => values.includes(value));
}

export function louvreSettingLabel(
  value: string | null | undefined,
  choices: string[] = [],
): string {
  if (!value || !airDirections.includes(value)) return 'Unknown';
  if (value === 'Auto') return 'Auto';
  if (value === 'Swing') return 'Swing / oscillate';
  const names: Record<string, string> = {
    Horizontal: 'Horizontal',
    Mid0: 'Upper intermediate',
    Mid1: 'Middle',
    Mid2: 'Lower intermediate',
    Vertical: 'Vertical',
  };
  const fixed = choices.filter(
    (choice) => choice !== 'Auto' && choice !== 'Swing',
  );
  const index = fixed.indexOf(value);
  return index < 0
    ? names[value]
    : `Position ${index + 1} of ${fixed.length} — ${names[value]}`;
}
